// Pure geometry for the scrolling tab row (TASK-610). The component feeds in
// measurements from the DOM; these decide what to show and where to scroll.

export interface ScrollBox {
    scrollLeft: number
    clientWidth: number
    scrollWidth: number
}

export interface TabBox {
    // offsetLeft / offsetWidth relative to the scrolling row
    left: number
    width: number
}

export interface ScrollEdges {
    // Content is hidden past the start (left) / end (right) edge.
    start: boolean
    end: boolean
}

// 1px slack: browsers report fractional scroll positions at the extremes.
const SLACK = 1

export const scrollEdges = ({ scrollLeft, clientWidth, scrollWidth }: ScrollBox): ScrollEdges => {
    const max = scrollWidth - clientWidth
    return { start: scrollLeft > SLACK, end: scrollLeft < max - SLACK }
}

// The scrollLeft that brings `tab` fully into view with `pad` px clear of the
// fade on the side it was hidden under, or null when no scroll is needed.
export const revealOffset = (box: ScrollBox, tab: TabBox, pad: number): number | null => {
    const max = box.scrollWidth - box.clientWidth
    if (max <= 0) return null

    const tabRight = tab.left + tab.width
    const visibleLeft = box.scrollLeft
    const visibleRight = visibleLeft + box.clientWidth

    let target: number | null = null
    if (tabRight + pad > visibleRight) target = tabRight + pad - box.clientWidth
    else if (tab.left - pad < visibleLeft) target = tab.left - pad
    if (target === null) return null

    target = Math.max(0, Math.min(max, target))
    return Math.abs(target - box.scrollLeft) < SLACK ? null : target
}

// Index of the tab an arrow click should reveal: moving forward, the first tab
// cut off on the right; moving back, the last tab cut off on the left.
export const stepTab = (box: ScrollBox, tabs: TabBox[], direction: 1 | -1): number | null => {
    const visibleLeft = box.scrollLeft
    const visibleRight = visibleLeft + box.clientWidth

    if (direction > 0) {
        const index = tabs.findIndex((tab) => tab.left + tab.width > visibleRight + SLACK)
        return index === -1 ? null : index
    }

    for (let index = tabs.length - 1; index >= 0; index--) {
        if (tabs[index].left < visibleLeft - SLACK) return index
    }
    return null
}

// Roving focus inside the row: ← → wrap around, Home/End jump. Null for keys
// the row doesn't handle.
export const rovingIndex = (key: string, current: number, count: number): number | null => {
    switch (key) {
        case 'ArrowRight':
            return (current + 1) % count
        case 'ArrowLeft':
            return (current - 1 + count) % count
        case 'Home':
            return 0
        case 'End':
            return count - 1
        default:
            return null
    }
}
